import { Injectable, Pipe, PipeTransform, Signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  EbuCarModelResponse,
  PackageResponse,
  PackageTagEnum,
  PackageTypeEnum,
  PriceTypeEnum,
  PropertyEnum,
  SelectedPackagesRequest,
  VehicleSearchResponse
} from "src/domain/client";
import { Store } from "@ngrx/store";
import {
  selectCarInfo,
  selectSelectedEbuCar
} from "../state/booking.selectors";
import { PriceService } from "../services/price.service";
import { PricePipe } from "./sharedpipes/price.pipe";

@Injectable({
  providedIn: "root"
})
@Pipe({
  name: "packagePrice",
  pure: false
})
export class PackagePricePipe implements PipeTransform {
  readonly selectedEbuCar: Signal<EbuCarModelResponse | null>;
  readonly carInfo: Signal<VehicleSearchResponse | null>;

  constructor(
    private priceService: PriceService,
    private translateService: TranslateService,
    private pricePipe: PricePipe,
    private store: Store
  ) {
    this.selectedEbuCar = this.store.selectSignal(selectSelectedEbuCar);
    this.carInfo = this.store.selectSignal(selectCarInfo);
  }

  transform(
    pkg: PackageResponse,
    selectedPackages: SelectedPackagesRequest[],
    summary = false,
    includeSubpackages = true,
    showSubCategoryPrice = false,
    isTotalPrice = false
  ): string {
    if (pkg == null) return "0 kr";
    const currentSelection = selectedPackages;
    const currentSelectedPackage = currentSelection.find(
      (s) => (s.packageId?.toString() || s.packageExternalId) === pkg.id
    );
    const hasSelectedSubPackage =
      !!currentSelectedPackage &&
      pkg.subPackages?.some((sp) => {
        return currentSelectedPackage.subPackageIds.includes(sp.id);
      });
    const basePriceString =
      !summary || pkg.type === PackageTypeEnum.BaseService
        ? this.pricePipe.transform(
            Math.round(
              this.priceService.getPriceOfPackage(
                pkg,
                currentSelection,
                includeSubpackages
              )
            )
          )
        : Math.round(pkg.price?.discounted);
    const total = isTotalPrice
      ? this.translateService.instant("summary.pay.total") + ": "
      : "";

    if (pkg.properties?.includes(PropertyEnum.EuropcarDropoff)) {
      const selectedCar = this.selectedEbuCar();
      if (selectedCar) {
        return (
          total +
          "fr. " +
          this.pricePipe.transform(Math.round(selectedCar.previewValue)) +
          " kr"
        );
      }
      return "";
    }

    if (
      pkg.type === PackageTypeEnum.OriginalService ||
      pkg.type === PackageTypeEnum.BaseService
    ) {
      if (
        pkg.tags.includes(PackageTagEnum.ServiceAgreement) &&
        this.carInfo()?.serviceAgreementResponse?.isSuccess
      )
        return this.translateService.instant("service-included") as string;
      if (pkg.price?.type === PriceTypeEnum.Offer)
        return this.translateService.instant("offer-price") as string;
      if (pkg.price?.type === PriceTypeEnum.From)
        return total + "fr. " + basePriceString + " kr";
      if (pkg.price?.type === PriceTypeEnum.Free)
        return this.translateService.instant("free-price") as string;
      if (pkg.price?.type === PriceTypeEnum.Fixed)
        return total + basePriceString + " kr";
      return this.pricePipe.transform(
        this.priceService.getPriceOfPackage(pkg, currentSelection) + " kr"
      );
    }
    if (pkg.isSubCategory && showSubCategoryPrice && includeSubpackages) {
      if (this.selectedSubPackagesIncludesOffer(pkg, currentSelectedPackage)) {
        return total + this.translateService.instant("offer-price");
      }
      return total + basePriceString + " kr";
    }
    if (pkg.type === PackageTypeEnum.TechUpdate)
      return this.translateService.instant("tech-update-price") as string;
    if (
      pkg.price?.type === PriceTypeEnum.Free &&
      !(isTotalPrice && hasSelectedSubPackage)
    )
      return this.translateService.instant("free-price") as string;
    if (
      pkg.price?.type === PriceTypeEnum.Deductibles &&
      !(isTotalPrice && hasSelectedSubPackage)
    )
      return this.translateService.instant("deductibles") as string;
    if (pkg.price?.type === PriceTypeEnum.Deductibles)
      return total + "fr. " + basePriceString + " kr";
    if (pkg.price?.type === PriceTypeEnum.Offer)
      return this.translateService.instant("offer-price") as string;

    if (pkg.price?.type === PriceTypeEnum.Fixed)
      return total + basePriceString + " kr";
    if (pkg.price?.type === PriceTypeEnum.From)
      return total + "fr. " + basePriceString + " kr";
    if (pkg.price?.type === PriceTypeEnum.Litre)
      return total + basePriceString + "kr/l";
    if (
      pkg.price?.type === PriceTypeEnum.Calculated &&
      pkg.price.price === 0 &&
      !!pkg.subPackages.length &&
      isTotalPrice
    ) {
      if (this.selectedSubPackagesIncludesOffer(pkg, currentSelectedPackage)) {
        return total + this.translateService.instant("offer-price");
      }
      return total + basePriceString + " kr";
    }
    if (pkg.price?.type === PriceTypeEnum.Calculated) {
      if (pkg.price.price === 0) return "";
      return this.pricePipe.transform(
        Math.round(this.priceService.getPriceOfPackage(pkg, currentSelection)) +
          " kr"
      );
    }
    return total + basePriceString + " kr";
  }

  private selectedSubPackagesIncludesOffer(
    pkg: PackageResponse,
    selection: SelectedPackagesRequest
  ): boolean {
    const values = pkg.subPackages
      .filter((sp) => !!selection?.subPackageIds?.includes(sp.id))
      .map((sp) => sp.price?.type);
    return values.length > 0 && values.every((t) => t === PriceTypeEnum.Offer);
  }

  static hasAsteriskPrice(
    selectedPackages: SelectedPackagesRequest[],
    basePackages: PackageResponse[],
    additionalPackages: PackageResponse[]
  ): boolean {
    if (!selectedPackages?.length) return false;

    const allPackages = [
      ...(basePackages || []),
      ...(additionalPackages || [])
    ];

    const selectedPackagesList = allPackages.filter((pkg) =>
      selectedPackages.find((v) => v.packageId === +pkg.id)
    );

    return !!selectedPackagesList.filter((j) =>
      j.subPackages?.length
        ? !!j.subPackages.filter(
            (p) =>
              p.price.type === PriceTypeEnum.From ||
              p.price.type === PriceTypeEnum.Offer ||
              p.price.type === PriceTypeEnum.Litre ||
              p.price.type === PriceTypeEnum.Deductibles ||
              p.properties?.includes(PropertyEnum.EuropcarDropoff)
          ).length
        : j.price?.type === PriceTypeEnum.From ||
          j.price?.type === PriceTypeEnum.Offer ||
          j.price?.type === PriceTypeEnum.Litre ||
          j.price?.type === PriceTypeEnum.Deductibles ||
          j.properties?.includes(PropertyEnum.EuropcarDropoff)
    ).length;
  }
}
